import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18n from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { reportClientError } from '@/lib/errorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /** Een chunk kon niet laden en we herladen de pagina; toon geen foutscherm. */
  reloading: boolean;
}

/**
 * Een chunk die niet wil laden is geen programmeerfout maar een
 * netwerkmoment: gym-wifi dat een request laat vallen, of een tabblad dat
 * nog de oude index.html heeft terwijl er net een nieuwe versie live ging
 * en de oude chunk-hashes niet meer bestaan. In beide gevallen lost één
 * herlaadbeurt het op. Zonder dit eindigde de gebruiker op "Something went
 * wrong" met een knop die hetzelfde deed als wat we hier automatisch doen.
 */
const CHUNK_LOAD_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /loading (css )?chunk/i,
  /chunkloaderror/i,
  // Een chunk-URL die HTML teruggeeft (index.html als fallback) parseert als
  // JavaScript en struikelt over de eerste '<'.
  /unexpected token '<'/i,
];

const RELOAD_GUARD_KEY = 'fa_chunk_reload_at';
/** Binnen dit venster herladen we niet nog een keer — dan is het geen netwerkmoment. */
const RELOAD_GUARD_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const text = `${(error as Error).name ?? ''} ${(error as Error).message ?? ''}`;
  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Probeer één keer te herladen. Geeft true terug als de herlaadbeurt is
 * gestart, false als we dat net al deden (dan is het echt stuk en hoort de
 * gebruiker het foutscherm te zien).
 */
function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage kan gooien (privémodus). Dan toch één keer proberen;
    // de lus die we vrezen kan zonder opslag ook niet worden herkend, dus
    // liever één extra reload dan een gebruiker die vastzit.
  }
  window.location.reload();
  return true;
}

/**
 * Global Error Boundary for crash visibility
 * Catches React rendering errors and displays a user-friendly fallback
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      reloading: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return { hasError: true, error, reloading: true };
    }
    return { hasError: true, error, reloading: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Een herlaadbeurt is onderweg; niets loggen, dit is geen fout van ons.
    if (this.state.reloading) return;

    // Log to console for debugging
    console.error('[ErrorBoundary] Uncaught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // Log error details (without PII)
    this.logError(error, errorInfo);
  }

  private logError(error: Error, errorInfo: ErrorInfo) {
    try {
      // Get current route
      const route = window.location.pathname;

      // Create sanitized error log
      const errorLog = {
        message: error.message,
        name: error.name,
        stack: error.stack?.substring(0, 500), // Truncate stack
        route,
        componentStack: errorInfo.componentStack?.substring(0, 500),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };

      // Log to console in development
      if (import.meta.env.DEV) {
        console.error('[ErrorBoundary] Error log:', errorLog);
      }

      // Production reporter: fires once, never throws, never PII.
      // No-op in dev (it would only console.error a duplicate).
      reportClientError({
        message: errorLog.message,
        name: errorLog.name,
        stack: errorLog.stack,
        route: errorLog.route,
        componentStack: errorLog.componentStack,
        userAgent: errorLog.userAgent,
        timestamp: errorLog.timestamp,
      });
    } catch (logError) {
      console.error('[ErrorBoundary] Failed to log error:', logError);
    }
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.reloading) {
      // Korte tussenstand terwijl de pagina herlaadt; geen rood, geen knop.
      return (
        <div
          className="min-h-screen flex items-center justify-center p-4 bg-background"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            {i18n.t('errorBoundary.reloading')}
          </p>
        </div>
      );
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full" data-testid="error-boundary-fallback">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle className="text-xl">{i18n.t('errorBoundary.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-center text-sm">
                {i18n.t('errorBoundary.body')}
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div className="p-3 rounded-lg bg-muted-bg text-xs font-mono overflow-auto max-h-40">
                  <p className="font-semibold text-destructive">{this.state.error.name}</p>
                  <p className="text-muted-foreground">{this.state.error.message}</p>
                </div>
              )}

              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" onClick={this.handleGoHome} className="min-h-11">
                  {i18n.t('errorBoundary.go_home')}
                </Button>
                <Button onClick={this.handleRefresh} className="min-h-11">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {i18n.t('errorBoundary.refresh')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
